import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
/*istanbul ignore next */
function DocumentViewer(props) {
  const { documents, tempId, previewType, height } = props;
  const [show, setShow] = useState(false);

  // PreviewType 1: First page only
  // PreviewType 2 or undefined: Show all pages
  const isFirstPageOnly = previewType === 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [tempId]);

  useEffect(() => {
    if (!show) return undefined;
    if (documents[0]?.fileType !== "pdf") return undefined;

    // Defensive textLayer styling. The pdf.js textLayer is an invisible,
    // selectable overlay positioned on top of the rendered canvas. If its
    // stylesheet (react-pdf/dist/Page/TextLayer.css) is missing or gets
    // overridden, the absolutely-positioned spans inside it fall back to
    // inline flow and render as visible text below the PDF page (showing
    // headlines, dates, etc. that came from the PDF). These rules force the
    // overlay back into shape regardless of load order.
    const styleId = "react-pdf-text-layer-fix";
    if (document.getElementById(styleId)) return undefined;
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .react-pdf__Page__textContent.textLayer,
      .react-pdf__Page .textLayer {
        position: absolute !important;
        inset: 0 !important;
        overflow: hidden !important;
        line-height: 1 !important;
        opacity: 1 !important;
        pointer-events: auto;
      }
      .react-pdf__Page__textContent.textLayer span,
      .react-pdf__Page__textContent.textLayer br,
      .react-pdf__Page .textLayer span,
      .react-pdf__Page .textLayer br {
        color: transparent !important;
        position: absolute !important;
        white-space: pre !important;
        cursor: text;
        margin: 0 !important;
        transform-origin: 0 0;
      }
      .react-pdf__Page__textContent.textLayer .endOfContent,
      .react-pdf__Page .textLayer .endOfContent {
        display: block;
        position: absolute;
        inset: 100% 0 0;
        z-index: -1;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, [show, documents]);

  useEffect(() => {
    if (isFirstPageOnly && show && documents[0]?.fileType === "pdf") {
      // Add CSS to hide scrolling and navigation for PDF first page preview
      const style = document.createElement("style");
      style.id = "pdf-first-page-restriction";
      style.innerHTML = `
        #pdf-controls,
        #pdf-controls-pagination,
        [id*="pdf-page-controls"],
        [id*="pdf-pagination"],
        button[aria-label*="Previous"],
        button[aria-label*="Next"] {
          display: none !important;
        }
        #pdf-page-wrapper,
        [id*="pdf-page-wrapper"] {
          max-height: calc(100vh - 100px) !important;
          overflow: hidden !important;
        }
        .react-pdf__Document {
          max-height: calc(100vh - 100px) !important;
          overflow: hidden !important;
        }
        .pdf-viewer-container,
        [class*="pdf-viewer"] {
          max-height: calc(100vh - 100px) !important;
          overflow: hidden !important;
        }
        /* Hide all pages except the first one */
        #pdf-page-wrapper > *:not(:first-child),
        .react-pdf__Document > *:not(:first-child) {
          display: none !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        const existingStyle = document.getElementById(
          "pdf-first-page-restriction",
        );
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [isFirstPageOnly, show, documents]);

  return (
    <div>
      {show && (
        <DocViewer
          prefetchMethod="GET"
          documents={documents}
          pluginRenderers={DocViewerRenderers}
          theme={{
            primary: "#5296d8",
            secondary: "#ffffff",
            tertiary: "#5296d899",
            textPrimary: "#ffffff",
            textSecondary: "#5296d8",
            textTertiary: "#00000099",
            disableThemeScrollbar: false,
          }}
          style={{ height: height ?? 1000 }}
          config={{
            pdfVerticalScrollByDefault: false,
          }}
        />
      )}
    </div>
  );
}

DocumentViewer.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      uri: PropTypes.string,
      fileType: PropTypes.string,
      fileName: PropTypes.string,
    }),
  ).isRequired,
  tempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  previewType: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
export default DocumentViewer;
